import { AxiosError } from 'axios'
import { AuthModel } from './_models'
import Swal from 'sweetalert2'
import { LOGIN_URL } from './_requests'
import { handleAPIErrors } from '../../../common/ErrorHandler'
import { isDisputeMutationUrl } from './disputeMutationUrl'
import { isSessionExpired } from './sessionExpiry'
import { showsItsOwnErrors } from './ownErrorUrls'

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
const getAuth = (): AuthModel | undefined => {
  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel
    if (auth) {
      // You can easily check auth_token expiration also
      return auth
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
  }
}

const setAuth = (auth: AuthModel) => {
  if (!localStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(auth)
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
  }
}

const removeAuth = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}

export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = 'application/json'

  axios.interceptors.request.use(
    (config: { headers: { 'x-api-key': string } }) => {
      const auth = getAuth()

      if (auth && auth.auth_token) {
        config.headers['x-api-key'] = auth.auth_token
      }

      return config
    },
    (error: any) => Promise.reject(error)
  )

  axios.interceptors.response.use(
    (response: any) => response,
    (error: AxiosError) => {
      if (!error.response) {
        Swal.fire('Ошибка сети', 'Не удалось подключиться к серверу', 'error')
        return Promise.reject(error)
      }

      const status = error.response?.status
      const url = error.config.url

      if (status === 403 && !showsItsOwnErrors(url, LOGIN_URL)) {
        Swal.fire('У вас нет доступа к данному действию', '', 'warning')
      }

      if (isSessionExpired(status, error.response?.data)) {
        removeAuth()

        if (window.location.pathname !== '/auth') {
          window.location.reload()
        }
      }

      if (
        // Аудит интеграций показывает текст прокси («не ответил за 5 секунд»), а не код ответа.
        !url?.includes('/internal/audit/') &&
        status !== 400 &&
        !isDisputeMutationUrl(url) &&
        // The credited amount change window shows its own text.
        !url?.includes('/credited-amount') &&
        // Справочники причин показывают свой текст на 409 REORDER_SET_MISMATCH; без исключения
        // сверху ложится техническое «Request failed with status code 409».
        !url?.includes('-reason/reorder') &&
        // The deposit cancel reason screen shows its own text for 409/404 and field errors.
        !url?.includes('/internal/deposit-cancel-reason') &&
        // Вход, страница учётной записи, белый список IP и второй фактор сотрудников показывают
        // свой текст ошибки на месте.
        !showsItsOwnErrors(url, LOGIN_URL) &&
        // Настройки системы показывают текст ошибки на самой странице — в таблице и под
        // полем; общий алерт поверх него сообщал бы только код ответа.
        !url?.includes('/internal/settings') &&
        !url?.includes('/internal/payment-adapter/') &&
        !url?.includes('/internal/matching/profiles') &&
        // The concierge window shows its own refusal text.
        !url?.includes('/concierge') &&
        !url?.includes('/ledger-wallet-balance') &&
        // Подсказка со снимком клиента открывается наведением и пишет свою ошибку внутри себя.
        !url?.includes('/client-snapshot/') &&
        !url?.includes('/ledger-deposits/') &&
        !url?.includes('/balance-policy') &&
        !url?.includes('/logs/risk-score-changes') &&
        // Карточка тикета показывает свой текст ошибки на самой странице («Тикет не найден»);
        // общий алерт поверх него сообщал бы только код ответа.
        !url?.includes('/internal/ticket/') &&
        // Списки и карточки отказов показывают свой текст («Отказ не найден») на странице.
        !url?.includes('/internal/payout-refusal') &&
        !url?.includes('/internal/matching/payout-refusal')
      ) {
        Swal.fire('Ошибка', 'Произошла ошибка на сервере: ' + error.message, 'error')
      }

      if (
        !url?.includes('/internal/audit/') &&
        status === 400 &&
        !isDisputeMutationUrl(url) &&
        !url?.includes('/credited-amount') &&
        !url?.includes('-reason/reorder') &&
        // The deposit cancel reason screen shows its own text for 409/404 and field errors.
        !url?.includes('/internal/deposit-cancel-reason') &&
        // Вход, страница учётной записи, белый список IP и второй фактор сотрудников показывают
        // свой текст ошибки на месте.
        !showsItsOwnErrors(url, LOGIN_URL) &&
        !url?.includes('/internal/settings') &&
        !url?.includes('/concierge') &&
        !url?.includes('/ledger-wallet-balance') &&
        // Подсказка со снимком клиента открывается наведением и пишет свою ошибку внутри себя.
        !url?.includes('/client-snapshot/') &&
        !url?.includes('/ledger-deposits/') &&
        !url?.includes('/balance-policy') &&
        !url?.includes('/logs/risk-score-changes') &&
        !url?.includes('/internal/ticket/') &&
        !url?.includes('/internal/payout-refusal') &&
        !url?.includes('/internal/matching/payout-refusal')
      ) {
        handleAPIErrors(error.response?.data.errors, status)
      }

      return Promise.reject(error.response)
    }
  )
}

export { getAuth, setAuth, removeAuth, AUTH_LOCAL_STORAGE_KEY }
